import { getAnonymousLdUser, getSignedInLdUser } from "@src/appV2/FeatureFlags/cbhUserContext";
import { SessionType } from "@src/modules/interface";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";

interface InitializeLaunchDarklyProps {
  profile?: SessionType["session"]["profile"];
  isAdmin: boolean;
  adminUserId?: string;
  workplaceId?: string;
}

interface InitializeLaunchDarklyResult {
  isIdentified: boolean;
}

export function useInitializeLaunchDarkly(
  props: InitializeLaunchDarklyProps
): InitializeLaunchDarklyResult {
  const { profile, isAdmin, adminUserId, workplaceId } = props;
  const ldClient = useLDClient();
  const [isIdentified, setIsIdentified] = useState(false);

  useEffect(() => {
    if (!ldClient) {
      return;
    }

    setIsIdentified(false);

    void (async () => {
      const ldUser = !profile?.userId
        ? getAnonymousLdUser()
        : getSignedInLdUser({
            userId: profile?.userId ?? "",
            name: profile?.name ?? "",
            email: profile?.email ?? "",
            msa:
              profile?.fullAddress?.metropolitanStatisticalArea ??
              profile?.address?.metropolitanStatisticalArea ??
              "",
            pricingMsa: profile?.pricingMsa ?? "",
            city: profile?.fullAddress?.city ?? "",
            state: profile?.fullAddress?.state ?? "",
            stateCode: profile?.fullAddress?.stateCode ?? "",
            type: profile?.type ?? "",
            hcfWebVersion: __APP_VERSION__,
            userType: !isAdmin ? "WORKPLACE_USER" : "CBH_EMPLOYEE",
            adminUserId,
            parentFacilityId: profile?.parentFacilityId,
            workplaceId,
          });

      try {
        await ldClient.identify(ldUser);
      } catch {
        // LaunchDarkly client may not be fully initialized yet, ignore the error
        // The client will eventually sync when ready
      } finally {
        setIsIdentified(true);
      }
    })();
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ldClient,
    profile?.userId,
    profile?.name,
    profile?.email,
    profile?.fullAddress?.metropolitanStatisticalArea,
    profile?.pricingMsa,
    profile?.fullAddress?.city,
    profile?.fullAddress?.state,
    profile?.fullAddress?.stateCode,
    profile?.type,
    profile?.parentFacilityId,
    isAdmin,
    adminUserId,
    workplaceId,
  ]);

  return { isIdentified };
}
